import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { Button, Flex, Text, Box } from "@radix-ui/themes";
import { useStore } from '../store/store';
import Icon from '@mdi/react';
import { mdiFileWord, mdiFilePdfBox, mdiLanguageMarkdown, mdiLoading } from '@mdi/js';
import api from '../services/api';

const SidebarExport = () => {
  const [state] = useStore();
  const [exportingDocx, setExportingDocx] = useState(false);
  const [exportingPdf, setExportingPdf] = useState(false);
  const [exportingMarkdown, setExportingMarkdown] = useState(false);
  const novelId = state.currentNovel?.id;
  
  // Function to export to docx
  const handleExportDocx = async () => {
    setExportingDocx(true);
    
    try {
      await api.exportNovelDocx(novelId);
    } catch (error) {
      console.error('Error exporting to DOCX:', error);
    } finally {
      // Set timeout to reset exporting state
      setTimeout(() => {
        setExportingDocx(false);
      }, 2000); // 2 second timeout to show loading state
    }
  };

  // Function to export to PDF
  const handleExportPdf = async () => {
    setExportingPdf(true);
    
    try {
      await api.exportNovelPdf(novelId);
    } catch (error) {
      console.error('Error exporting to PDF:', error);
    } finally {
      // Set timeout to reset exporting state
      setTimeout(() => {
        setExportingPdf(false);
      }, 2000); // 2 second timeout to show loading state
    }
  };
  
  // Function to export to Markdown
  const handleExportMarkdown = async () => {
    setExportingMarkdown(true);
    
    try {
      await api.exportNovelMarkdown(novelId);
    } catch (error) {
      console.error('Error exporting to Markdown:', error);
    } finally {
      // Set timeout to reset exporting state
      setTimeout(() => {
        setExportingMarkdown(false);
      }, 2000); // 2 second timeout to show loading state
    }
  };

  const novelTitle = state.currentNovel?.title || 'Novel';
  const wordCount = state.currentNovel?.word_count || 0;
  const chapterCount = state.currentNovel?.chapters?.length || 0;

  return (
    <div className="sidebar-export-container">      
      <Flex direction="column" gap="3">
        <Button 
          className="sidebar-export-button"
          size="3"
          style={{
            justifyContent: 'flex-start',
            backgroundColor: exportingDocx ? '#3a4749' : '#4b6a9a',
            color: 'white',
            cursor: exportingDocx ? 'not-allowed' : 'pointer'
          }}
          onClick={handleExportDocx}
          disabled={exportingDocx || exportingPdf || exportingMarkdown}
        >
          <Icon 
            path={exportingDocx ? mdiLoading : mdiFileWord} 
            size={1}
            className={exportingDocx ? 'sidebar-export-rotating' : ''}
          />
          {exportingDocx ? 'Exporting to Word...' : 'Export to Word (.docx)'}
        </Button>
        
        <Button 
          className="sidebar-export-button"
          size="3"
          style={{
            justifyContent: 'flex-start',
            backgroundColor: exportingPdf ? '#3a4749' : '#a93030',
            color: 'white',
            cursor: exportingPdf ? 'not-allowed' : 'pointer'
          }}
          onClick={handleExportPdf}
          disabled={exportingDocx || exportingPdf || exportingMarkdown}
        >
          <Icon 
            path={exportingPdf ? mdiLoading : mdiFilePdfBox} 
            size={1}
            className={exportingPdf ? 'sidebar-export-rotating' : ''}
          />
          {exportingPdf ? 'Exporting to PDF...' : 'Export to PDF (.pdf)'}
        </Button>
        
        <Button 
          className="sidebar-export-button"
          size="3"
          style={{
            justifyContent: 'flex-start',
            backgroundColor: exportingMarkdown ? '#3a4749' : '#4c4c4c',
            color: 'white',
            cursor: exportingMarkdown ? 'not-allowed' : 'pointer'
          }}
          onClick={handleExportMarkdown}
          disabled={exportingDocx || exportingPdf || exportingMarkdown}
        >
          <Icon 
            path={exportingMarkdown ? mdiLoading : mdiLanguageMarkdown} 
            size={1}
            className={exportingMarkdown ? 'sidebar-export-rotating' : ''}
          />
          {exportingMarkdown ? 'Exporting to Markdown...' : 'Export to Markdown (.md)'}
        </Button>
      </Flex>
      
      <Box mt="4" p="3" style={{backgroundColor: '#3c4748', borderRadius: '8px'}}>
        <Text size="2" as="div" weight="bold" style={{color: 'white', marginBottom: '8px'}}>
          Format Information
        </Text>
        <ul style={{color: 'white', opacity: 0.8, paddingLeft: '20px', fontSize: '14px'}}>
          <li><strong>Word (.docx)</strong>: Best for further editing in word processors</li>
          <li><strong>PDF (.pdf)</strong>: Best for sharing and printing</li>
          <li><strong>Markdown (.md)</strong>: Plain text with formatting codes</li>
        </ul>
      </Box>

      <style jsx>{`
        .sidebar-export-container {
          padding: 0 10px 20px 10px;
        }
        
        .sidebar-export-button {
          display: flex;
          align-items: center;
          gap: 12px;
          transition: transform 0.2s, box-shadow 0.2s;
        }
        
        .sidebar-export-button:not([disabled]):hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
        }
        
        .sidebar-export-rotating {
          animation: sidebar-export-rotate 1.5s linear infinite;
        }
        
        @keyframes sidebar-export-rotate {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default SidebarExport;
